import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { CalendarDays, Clock, User, Smartphone, Activity, ChevronLeft, ChevronRight, Instagram, Hash, Heart, Users, Lock, SkipForward, UserPlus, UserMinus, Zap, Eye, Sparkles, Target } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import SharedPageHeader from '../components/layout/PageHeader';
import { getProfilePicUrl, getLocalProfilePicUrl } from '../config/imageUrls';
import { apiUrl } from '../config/api';

const PageContainer = styled.div`
  padding: 20px;
`;

const DateSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--card-bg);
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid var(--card-border);
`;

const DateInput = styled.input`
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: 6px;
  padding: 6px 10px;
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 130px;
  &:hover { border-color: var(--primary); }
  &:focus { outline: none; border-color: var(--primary); }
  &::-webkit-calendar-picker-indicator { filter: invert(1); cursor: pointer; }
`;

const DateButton = styled.button`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 6px;
  padding: 6px 8px;
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  &:hover { background: var(--bg); border-color: var(--primary); color: var(--text-primary); }
  &:active { transform: scale(0.95); }
`;

const TopBar = styled.div`
  display: flex; align-items: center; gap: 16px; margin-bottom: 16px; flex-wrap: wrap;
`;

const StatsRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 16px;
`;

const MiniStatCard = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 10px;
  padding: 12px 16px; display: flex; align-items: center; gap: 10px; flex: 1; min-width: 140px;
  transition: all 0.2s ease;
  &:hover { border-color: var(--primary); }
`;

const MiniStatIcon = styled.div`
  width: 30px; height: 30px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
`;

const MiniStatInfo = styled.div`
  display: flex; flex-direction: column;
`;

const MiniStatLabel = styled.div`
  font-size: 10px; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.3px;
`;

const MiniStatValue = styled.div`
  font-size: 20px; font-weight: 900;
`;

const ContentLayout = styled.div`
  display: grid; grid-template-columns: 1fr 420px; gap: 20px; align-items: start;
`;

const TableContainer = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 8px;
  overflow: hidden; box-shadow: 0 4px 12px rgba(165, 145, 239, 0.3); transition: all 0.3s ease;
  &:hover { box-shadow: 0 6px 16px rgba(165, 145, 239, 0.3); border-color: var(--primary); }
`;

const DetailPanel = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 8px;
  padding: 20px; position: sticky; top: 20px;
  box-shadow: 0 4px 12px rgba(165, 145, 239, 0.3);
`;

const DetailHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid var(--card-border);
`;

const DetailTitle = styled.h3`
  font-size: 15px; font-weight: 700; color: var(--text-primary); margin: 0;
  display: flex; align-items: center; gap: 10px;
`;

const ActionsList = styled.div`
  display: flex; flex-direction: column; gap: 10px;
`;

const ActionItem = styled.div`
  background: var(--bg); border: 1px solid var(--card-border); border-radius: 6px;
  padding: 10px 12px; transition: all 0.2s ease;
  &:hover { border-color: var(--primary); background: var(--card-bg); }
`;

const ActionType = styled.div`
  font-size: 11px; font-weight: 700; color: var(--primary); text-transform: uppercase; margin-bottom: 4px;
`;

const ActionTarget = styled.div`
  font-size: 13px; font-weight: 600; color: var(--text-primary); margin-bottom: 3px;
`;

const ActionTime = styled.div`
  font-size: 11px; color: var(--text-secondary);
`;

const SessionTypeBadge = styled.span`
  display: inline-flex; align-items: center; gap: 4px; padding: 3px 8px; border-radius: 4px;
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px;
  background: ${p => p.$color || 'rgba(165,145,239,.12)'};
  color: ${p => p.$textColor || 'var(--primary)'};
`;

const Table = styled.div`
  width: 100%; background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 8px; overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 50px 70px 32px 110px 64px 66px 52px 52px 46px 46px 48px 52px 48px 46px 40px 48px;
  gap: 4px; padding: 8px 12px; background: var(--bg); border-bottom: 1px solid var(--card-border);
  font-weight: 700; color: var(--text-secondary); font-size: 10px; text-transform: uppercase;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 50px 70px 32px 110px 64px 66px 52px 52px 46px 46px 48px 52px 48px 46px 40px 48px;
  gap: 4px; padding: 6px 12px; border-bottom: 1px solid var(--card-border); transition: all 0.15s ease;
  background: ${props => props.selected ? 'rgba(88, 101, 242, 0.1)' : 'var(--card-bg)'};
  cursor: pointer; align-items: center;
  &:hover { background: rgba(88, 101, 242, 0.15); }
  &:last-child { border-bottom: none; }
`;

const TableHead = styled.div`
  cursor: pointer; user-select: none; display: flex; flex-direction: column; gap: 2px;
  transition: all 0.2s ease; padding: 2px; border-radius: 4px;
  font-size: 9px; font-weight: 700; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.3px;
  &:hover { background: rgba(88, 101, 242, 0.1); color: var(--text-primary); }
  svg { flex-shrink: 0; }
`;

const TableCell = styled.div`
  font-size: 11px; color: var(--text-primary); font-weight: 500;
  display: flex; align-items: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  strong { font-weight: 700; }
`;

const ProfileImage = styled.img`
  width: 28px; height: 28px; border-radius: 50%; object-fit: cover;
  border: 2px solid var(--card-border); margin-right: 0; transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(165, 145, 239, 0.2);
  &:hover { transform: scale(1.1); border-color: var(--primary); box-shadow: 0 3px 6px rgba(88, 101, 242, 0.4); }
`;

const ProfilePlaceholder = styled.div`
  width: 28px; height: 28px; border-radius: 50%;
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  display: inline-flex; align-items: center; justify-content: center;
  color: white; font-weight: 700; font-size: 11px;
  border: 2px solid var(--card-border); margin-right: 0;
  box-shadow: 0 1px 3px rgba(165, 145, 239, 0.2);
`;

const LoadingMessage = styled.div`
  text-align: center; padding: 40px; font-size: 16px; color: var(--text-secondary);
`;

const Sessions = () => {
  const { theme } = useTheme();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionActions, setSessionActions] = useState([]);
  const [loadingActions, setLoadingActions] = useState(false);
  const [timeUnit, setTimeUnit] = useState('min'); // 'min' or 'hour'
  const [stats, setStats] = useState({
    totalLikes: 0, totalFollows: 0, totalUnfollows: 0,
    totalStoryViews: 0, totalStoryLikes: 0,
    totalUniqueUsers: 0, totalPrivateProfiles: 0,
    totalSkipped: 0, totalTargets: 0, totalGetProfile: 0,
    totalComments: 0, totalDms: 0
  });

  useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    // Sélectionner automatiquement la dernière session
    if (sessions.length > 0 && !selectedSession) {
      handleSessionClick(sessions[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const response = await fetch(apiUrl(`/api/sessions?date=${selectedDate}`));
      const data = await response.json();
      
      const list = data.sessions || [];
      setSessions(list);
      
      // Calculer les stats
      const sum = (key) => list.reduce((s, x) => s + (x[key] || 0), 0);
      setStats({
        totalLikes: sum('likes'), totalFollows: sum('follows'), totalUnfollows: sum('unfollows'),
        totalStoryViews: sum('story_views'), totalStoryLikes: sum('story_likes'),
        totalUniqueUsers: sum('unique_users'), totalPrivateProfiles: sum('total_private_profiles'),
        totalSkipped: sum('total_skipped_recent'), totalScraped: sum('total_scraped'),
        totalGetProfile: sum('get_profiles'), totalComments: sum('comments'), totalDms: sum('dms')
      });
    } catch (error) {
      console.error('Erreur lors du chargement des sessions:', error);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes.toString().padStart(2, '0')}min`;
    }
    return `${minutes}min`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleTimeString('fr-FR', { 
      timeZone: 'Europe/Paris',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const changeDate = (days) => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + days);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  const handleSessionClick = async (session) => {
    setSelectedSession(session);
    setLoadingActions(true);
    
    try {
      const response = await fetch(apiUrl(`/api/session-actions/${session.id}`));
      const data = await response.json();
      setSessionActions(data.actions || []);
    } catch (error) {
      console.error('Erreur lors du chargement des actions:', error);
      setSessionActions([]);
    } finally {
      setLoadingActions(false);
    }
  };

  const getActionIcon = (type) => {
    switch(type) {
      case 'LikeAction': return '❤️';
      case 'FollowAction': return '➕';
      case 'UnfollowAction': return '➖';
      case 'CommentAction': return '💬';
      case 'StoryLikeAction': return '⭐';
      case 'StoryWatchAction': return '👁️';
      case 'GetProfileAction': return '👤';
      case 'DirectMessageAction': return '✉️';
      default: return '📌';
    }
  };

  const getActionLabel = (type) => {
    switch(type) {
      case 'LikeAction': return 'Like';
      case 'FollowAction': return 'Follow';
      case 'UnfollowAction': return 'Unfollow';
      case 'CommentAction': return 'Comment';
      case 'StoryLikeAction': return 'Story Like';
      case 'StoryWatchAction': return 'Story Watch';
      case 'GetProfileAction': return 'Profile View';
      case 'DirectMessageAction': return 'Direct Message';
      default: return type;
    }
  };

  return (
    <PageContainer>
      <SharedPageHeader icon={CalendarDays} title="Sessions" subtitle="Historique des sessions Instagram" />

      <TopBar>
        <DateSelector>
          <DateButton onClick={() => changeDate(-1)}><ChevronLeft size={18} /></DateButton>
          <DateInput type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
          <DateButton onClick={() => changeDate(1)}><ChevronRight size={18} /></DateButton>
        </DateSelector>
        <div style={{ color: 'var(--text-muted)', fontSize: '12px', fontWeight: '600' }}>
          {sessions.length} session{sessions.length !== 1 ? 's' : ''}
        </div>
      </TopBar>

      <StatsRow>
        {[
          { icon: Heart, label: 'Likes', value: stats.totalLikes, color: '#ff6b9d', bg: 'rgba(255,107,157,.15)' },
          { icon: UserPlus, label: 'Follows', value: stats.totalFollows, color: '#22c55e', bg: 'rgba(34,197,94,.15)' },
          { icon: UserMinus, label: 'Unfollows', value: stats.totalUnfollows, color: '#ef4444', bg: 'rgba(239,68,68,.15)' },
          { icon: Eye, label: 'Story Views', value: stats.totalStoryViews, color: '#f59e0b', bg: 'rgba(245,158,11,.15)' },
          { icon: Sparkles, label: 'Story Likes', value: stats.totalStoryLikes, color: '#ec4899', bg: 'rgba(236,72,153,.15)' },
        ].map((s, i) => (
          <MiniStatCard key={i}>
            <MiniStatIcon style={{ background: s.bg, color: s.color }}><s.icon size={16} /></MiniStatIcon>
            <MiniStatInfo>
              <MiniStatLabel>{s.label}</MiniStatLabel>
              <MiniStatValue style={{ color: s.color }}>{s.value}</MiniStatValue>
            </MiniStatInfo>
          </MiniStatCard>
        ))}
      </StatsRow>

      <StatsRow>
        {[
          { icon: Users, label: 'Users Uniques', value: stats.totalUniqueUsers, color: 'var(--primary)', bg: 'rgba(165,145,239,.15)' },
          { icon: Lock, label: 'Profils Privés', value: stats.totalPrivateProfiles, color: '#f59e0b', bg: 'rgba(245,158,11,.15)' },
          { icon: SkipForward, label: 'Skippés', value: stats.totalSkipped, color: '#6b7280', bg: 'rgba(107,114,128,.15)' },
          { icon: User, label: 'Profils Visités', value: stats.totalGetProfile, color: '#3b82f6', bg: 'rgba(59,130,246,.15)' },
          { icon: Target, label: 'Scrappés', value: stats.totalScraped, color: '#a855f7', bg: 'rgba(168,85,247,.15)' },
        ].map((s, i) => (
          <MiniStatCard key={i}>
            <MiniStatIcon style={{ background: s.bg, color: s.color }}><s.icon size={16} /></MiniStatIcon>
            <MiniStatInfo>
              <MiniStatLabel>{s.label}</MiniStatLabel>
              <MiniStatValue style={{ color: s.color }}>{s.value}</MiniStatValue>
            </MiniStatInfo>
          </MiniStatCard>
        ))}
      </StatsRow>

      <ContentLayout>
        <TableContainer>
          {loading ? (
            <LoadingMessage>⏳ Chargement des sessions...</LoadingMessage>
          ) : sessions.length === 0 ? (
            <LoadingMessage>Aucune session trouvée pour cette date</LoadingMessage>
          ) : (
            <Table>
              <TableHeader>
                <TableHead>
                  <Hash size={12} />
                  ID
                </TableHead>
                <TableHead>
                  <Smartphone size={12} />
                  Device
                </TableHead>
                <TableHead>
                  <User size={12} />
                </TableHead>
                <TableHead>
                  <Instagram size={12} />
                  Username
                </TableHead>
                <TableHead>
                  <Zap size={12} />
                  Type
                </TableHead>
                <TableHead>
                  <Clock size={12} />
                  Durée
                </TableHead>
                <TableHead>
                  <Clock size={12} />
                  Début
                </TableHead>
                <TableHead>
                  <Clock size={12} />
                  Fin
                </TableHead>
                <TableHead>
                  <Users size={12} />
                  Profils
                </TableHead>
                <TableHead>
                  <Heart size={12} />
                  Likes
                </TableHead>
                <TableHead>
                  <UserPlus size={12} />
                  Follows
                </TableHead>
                <TableHead>
                  <UserMinus size={12} />
                  Unfollows
                </TableHead>
                <TableHead>
                  <Sparkles size={12} />
                  Uniques
                </TableHead>
                <TableHead>
                  <Lock size={12} />
                  Privés
                </TableHead>
                <TableHead>
                  <SkipForward size={12} />
                  Skip
                </TableHead>
                <TableHead>
                  <Target size={12} />
                  Scrapes
                </TableHead>
              </TableHeader>
              <div>
                {sessions.map((session) => (
                  <TableRow 
                    key={session.id} 
                    theme={theme}
                    selected={selectedSession?.id === session.id}
                    onClick={() => handleSessionClick(session)}
                  >
                    <TableCell theme={theme}>
                      <span style={{color: 'var(--primary)', fontWeight: '700', fontFamily: 'monospace', fontSize: '11px'}}>{session.id}</span>
                    </TableCell>
                    <TableCell theme={theme} style={{fontSize: '10px'}}>{session.device_id || 'N/A'}</TableCell>
                    <TableCell theme={theme}>
                      {session.profile_name ? (
                        <ProfileImage 
                          theme={theme}
                          src={getLocalProfilePicUrl(session.profile_name)}
                          alt={session.profile_name}
                          onError={(e) => {
                            const step = parseInt(e.target.dataset.fb || '0');
                            if (step === 0) { e.target.dataset.fb='1'; e.target.src=getProfilePicUrl(session.profile_name); return; }
                            if (step === 1) { e.target.dataset.fb='2'; e.target.src=`https://unavatar.io/instagram/${session.profile_name}`; return; }
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'inline-flex';
                          }}
                        />
                      ) : null}
                      <ProfilePlaceholder style={{ display: session.profile_name ? 'none' : 'inline-flex' }}>
                        {session.profile_name ? session.profile_name.charAt(0).toUpperCase() : '?'}
                      </ProfilePlaceholder>
                    </TableCell>
                    <TableCell theme={theme}>
                      <strong style={{fontSize: '11px'}}>@{session.profile_name}</strong>
                    </TableCell>
                    <TableCell theme={theme}>
                      <span style={{
                        fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', padding: '2px 6px', borderRadius: '4px',
                        background: session.session_type === 'interact' ? 'rgba(34,197,94,.12)' : session.session_type === 'unfollow' ? 'rgba(239,68,68,.12)' : session.session_type === 'scrape' ? 'rgba(168,85,247,.12)' : 'rgba(165,145,239,.12)',
                        color: session.session_type === 'interact' ? '#22c55e' : session.session_type === 'unfollow' ? '#ef4444' : session.session_type === 'scrape' ? '#a855f7' : 'var(--primary)'
                      }}>{session.session_type || '—'}</span>
                    </TableCell>
                    <TableCell theme={theme}>
                      <span style={{fontSize: '11px', color: 'var(--btn-text)', fontWeight: '700'}}>{formatDuration(session.duration)}</span>
                    </TableCell>
                    <TableCell theme={theme}>
                      <span style={{fontSize: '11px', fontWeight: '700', color: 'var(--btn-text)'}}>
                        {formatTime(session.start_time)}
                      </span>
                    </TableCell>
                    <TableCell theme={theme}>
                      <span style={{fontSize: '11px', fontWeight: '700', color: 'var(--btn-text)'}}>
                        {formatTime(session.end_time)}
                      </span>
                    </TableCell>
                    <TableCell theme={theme}>
                      <span style={{fontWeight: '700', color: '#3b82f6'}}>{session.get_profiles || 0}</span>
                    </TableCell>
                    <TableCell theme={theme}>
                      <span style={{fontWeight: '700', color: '#ff6b9d'}}>{session.likes || 0}</span>
                    </TableCell>
                    <TableCell theme={theme}>
                      <span style={{fontWeight: '700', color: '#22c55e'}}>{session.follows || 0}</span>
                    </TableCell>
                    <TableCell theme={theme}>
                      <span style={{fontWeight: '700', color: '#ef4444'}}>{session.unfollows || 0}</span>
                    </TableCell>
                    <TableCell theme={theme}>
                      <span style={{fontWeight: '700', color: '#06b6d4'}}>{session.unique_users || 0}</span>
                    </TableCell>
                    <TableCell theme={theme}>
                      <span style={{fontWeight: '700', color: '#f59e0b'}}>{session.total_private_profiles || 0}</span>
                    </TableCell>
                    <TableCell theme={theme}>
                      <span style={{fontWeight: '700', color: '#6b7280'}}>{session.total_skipped_recent || 0}</span>
                    </TableCell>
                    <TableCell theme={theme}>
                      <span style={{fontWeight: '700', color: '#a855f7'}}>{session.total_scraped || 0}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </div>
            </Table>
          )}
        </TableContainer>

      {/* Right Panel - Session Details */}
      <DetailPanel>
        <DetailHeader>
          <DetailTitle>
            <Activity size={18} />
            Détails de la session
          </DetailTitle>
        </DetailHeader>

        {selectedSession ? (
          <>
            {/* Session info card */}
            <div style={{ marginBottom: '16px', padding: '14px', background: 'var(--bg)', borderRadius: '8px', border: '1px solid var(--card-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                {selectedSession.profile_name && (
                  <img 
                    src={getLocalProfilePicUrl(selectedSession.profile_name)}
                    alt={selectedSession.profile_name}
                    onError={(e) => {
                      const step = parseInt(e.target.dataset.fb || '0');
                      if (step === 0) { e.target.dataset.fb='1'; e.target.src=getProfilePicUrl(selectedSession.profile_name); return; }
                      if (step === 1) { e.target.dataset.fb='2'; e.target.src=`https://unavatar.io/instagram/${selectedSession.profile_name}`; return; }
                      e.target.style.display='none';
                    }}
                    style={{ width: '38px', height: '38px', borderRadius: '50%', border: '2px solid var(--card-border)', objectFit: 'cover' }}
                  />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ color: 'var(--text-primary)', fontWeight: '700', fontSize: '14px' }}>
                    @{selectedSession.profile_name}
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                    {selectedSession.device_id}
                  </div>
                </div>
                <SessionTypeBadge
                  $color={selectedSession.session_type === 'interact' ? 'rgba(34,197,94,.12)' : selectedSession.session_type === 'unfollow' ? 'rgba(239,68,68,.12)' : 'rgba(165,145,239,.12)'}
                  $textColor={selectedSession.session_type === 'interact' ? '#22c55e' : selectedSession.session_type === 'unfollow' ? '#ef4444' : 'var(--primary)'}
                >
                  <Zap size={10} />
                  {selectedSession.session_type || selectedSession.app_id || 'interact'}
                </SessionTypeBadge>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>Durée: </span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{formatDuration(selectedSession.duration)}</span>
                </div>
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>Début: </span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{formatTime(selectedSession.start_time)}</span>
                </div>
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>Fin: </span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{formatTime(selectedSession.end_time)}</span>
                </div>
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>ID: </span>
                  <span style={{ color: 'var(--primary)', fontWeight: '700', fontFamily: 'monospace', fontSize: '11px' }}>#{selectedSession.id}</span>
                </div>
                {selectedSession.app_id && selectedSession.app_id !== 'com.instagram.android' && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Package: </span>
                    <span style={{ color: '#f59e0b', fontWeight: '600', fontFamily: 'monospace', fontSize: '11px' }}>{selectedSession.app_id}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action counters grid */}
            {(() => {
              // unique_users = profiles actually interacted with (like, follow, comment, DM, story)
              const uniqueUsers = selectedSession.unique_users || 0;
              const dur = selectedSession.duration || 0;
              const divUnit = dur > 0 ? dur / (timeUnit === 'min' ? 60 : 3600) : 1;

              return (<>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ padding: '12px', background: 'var(--bg)', borderRadius: '8px', border: '1px solid var(--card-border)', textAlign: 'center' }}>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Total Actions</div>
                    <div style={{ color: 'var(--primary)', fontSize: '22px', fontWeight: '700' }}>{selectedSession.total_interactions || sessionActions.length || 0}</div>
                  </div>
                  <div style={{ padding: '12px', background: 'var(--bg)', borderRadius: '8px', border: '1px solid var(--card-border)', textAlign: 'center' }}>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Users Uniques</div>
                    <div style={{ color: '#8b5cf6', fontSize: '22px', fontWeight: '700' }}>{uniqueUsers}</div>
                  </div>
                </div>

                {/* Detailed action counters */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '6px', marginBottom: '14px' }}>
                  {[
                    { icon: Heart, label: 'Likes', value: selectedSession.likes || 0, color: '#ff6b9d' },
                    { icon: UserPlus, label: 'Follows', value: selectedSession.follows || 0, color: '#22c55e' },
                    { icon: UserMinus, label: 'Unfollows', value: selectedSession.unfollows || 0, color: '#ef4444' },
                    { icon: Eye, label: 'Story V.', value: selectedSession.story_views || 0, color: '#f59e0b' },
                    { icon: Sparkles, label: 'Story L.', value: selectedSession.story_likes || 0, color: '#ec4899' },
                    { icon: Users, label: 'Profils', value: selectedSession.get_profiles || 0, color: '#3b82f6' },
                    { icon: Lock, label: 'Privés', value: selectedSession.total_private_profiles || 0, color: '#f59e0b' },
                    { icon: SkipForward, label: 'Skippés', value: selectedSession.total_skipped_recent || 0, color: '#6b7280' },
                    ...(selectedSession.total_scraped > 0 ? [{ icon: Target, label: 'Scrapes', value: selectedSession.total_scraped, color: '#a855f7' }] : []),
                  ].map((item, i) => (
                    <div key={i} style={{ padding: '8px 4px', background: 'var(--bg)', borderRadius: '6px', border: '1px solid var(--card-border)', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px', color: 'var(--text-secondary)', fontSize: '9px', fontWeight: '600', marginBottom: '3px' }}>
                        <item.icon size={10} style={{ color: item.color }} />
                        {item.label}
                      </div>
                      <div style={{ color: item.color, fontWeight: '700', fontSize: '14px' }}>{item.value}</div>
                    </div>
                  ))}
                </div>

                {/* Statistics per minute/hour */}
                <div style={{ marginBottom: '14px', padding: '12px', background: 'var(--card-bg)', borderRadius: '8px', border: '1px solid var(--card-border)' }}>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '10px', cursor: 'pointer' }}
                    onClick={() => setTimeUnit(timeUnit === 'min' ? 'hour' : 'min')}
                  >
                    Stats par {timeUnit === 'min' ? 'minute' : 'heure'} ↻
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '6px', fontSize: '12px' }}>
                    {[
                      { icon: Heart, label: 'Likes', value: selectedSession.likes || 0, color: '#ff6b9d' },
                      { icon: UserPlus, label: 'Follows', value: selectedSession.follows || 0, color: '#22c55e' },
                      { icon: Users, label: 'Users', value: uniqueUsers, color: '#8b5cf6' },
                      { icon: Zap, label: 'Interact.', value: selectedSession.total_interactions || 0, color: 'var(--primary)' },
                      { icon: Eye, label: 'Story V.', value: selectedSession.story_views || 0, color: '#f59e0b' },
                      { icon: Sparkles, label: 'Story L.', value: selectedSession.story_likes || 0, color: '#ec4899' },
                      { icon: Target, label: 'Scrapes', value: selectedSession.total_scraped || 0, color: '#a855f7' },
                      { icon: UserMinus, label: 'Unfollows', value: selectedSession.unfollows || 0, color: '#ef4444' },
                    ].map((item, i) => (
                      <div key={i} style={{ textAlign: 'center', padding: '6px', background: 'var(--bg)', borderRadius: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px', color: 'var(--text-secondary)', fontSize: '9px', fontWeight: '600', marginBottom: '3px' }}>
                          <item.icon size={10} style={{ color: item.color }} />
                          {item.label}/{timeUnit === 'min' ? 'min' : 'h'}
                        </div>
                        <div style={{ color: item.color, fontWeight: '700', fontSize: '13px' }}>
                          {dur > 0 ? (item.value / divUnit).toFixed(1) : '0.0'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pie Chart - User Analysis */}
                <div style={{ marginBottom: '14px', padding: '16px', background: 'var(--card-bg)', borderRadius: '8px', border: '1px solid var(--card-border)' }}>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '12px', textAlign: 'center' }}>
                    Analyse des Utilisateurs
                  </div>
                  {(() => {
                    const interacted = uniqueUsers;
                    const privateUsers = selectedSession.total_private_profiles || 0;
                    const skippedUsers = selectedSession.total_skipped_recent || 0;
                    const emptyUsers = selectedSession.total_empty_profiles || 0;
                    const scrapedUsers = selectedSession.total_scraped || 0;
                    const getProfiles = selectedSession.get_profiles || 0;
                    const isScrape = selectedSession.session_type === 'scrape';
                    const totalSeen = getProfiles + skippedUsers;
                    const otherVisited = Math.max(0, getProfiles - interacted - privateUsers - emptyUsers - (isScrape ? scrapedUsers : 0));
                    const radius = 70;
                    const circumference = 2 * Math.PI * radius;
                    const scrapedDash = totalSeen > 0 ? (scrapedUsers / totalSeen) * circumference : 0;
                    const interactedDash = totalSeen > 0 ? (interacted / totalSeen) * circumference : 0;
                    const privateDash = totalSeen > 0 ? (privateUsers / totalSeen) * circumference : 0;
                    const skippedDash = totalSeen > 0 ? (skippedUsers / totalSeen) * circumference : 0;
                    const emptyDash = totalSeen > 0 ? (emptyUsers / totalSeen) * circumference : 0;
                    const otherDash = totalSeen > 0 ? (otherVisited / totalSeen) * circumference : 0;
                    let offset = 0;
                    const legend = [
                      ...(interacted > 0 ? [{ label: 'Interagis', color: 'var(--success)', count: interacted, pct: totalSeen > 0 ? ((interacted / totalSeen) * 100).toFixed(1) : '0' }] : []),
                      ...(scrapedUsers > 0 ? [{ label: 'Scrappés', color: '#a855f7', count: scrapedUsers, pct: totalSeen > 0 ? ((scrapedUsers / totalSeen) * 100).toFixed(1) : '0' }] : []),
                      ...(privateUsers > 0 ? [{ label: 'Privés', color: '#f59e0b', count: privateUsers, pct: totalSeen > 0 ? ((privateUsers / totalSeen) * 100).toFixed(1) : '0' }] : []),
                      ...(skippedUsers > 0 ? [{ label: 'Skippés', color: '#6b7280', count: skippedUsers, pct: totalSeen > 0 ? ((skippedUsers / totalSeen) * 100).toFixed(1) : '0' }] : []),
                      ...(emptyUsers > 0 ? [{ label: 'Vides', color: '#ef4444', count: emptyUsers, pct: totalSeen > 0 ? ((emptyUsers / totalSeen) * 100).toFixed(1) : '0' }] : []),
                      ...(otherVisited > 0 ? [{ label: 'Analysés', color: '#3b82f6', count: otherVisited, pct: totalSeen > 0 ? ((otherVisited / totalSeen) * 100).toFixed(1) : '0' }] : []),
                    ];
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px' }}>
                        <svg width="120" height="120" viewBox="0 0 160 160" style={{ transform: 'rotate(-90deg)' }}>
                          <circle cx="80" cy="80" r={radius} fill="none" stroke="var(--card-border)" strokeWidth="20" />
                          {interacted > 0 && <circle cx="80" cy="80" r={radius} fill="none" stroke="var(--success)" strokeWidth="20" strokeDasharray={`${interactedDash} ${circumference}`} strokeDashoffset={-offset} />}
                          {scrapedUsers > 0 && (() => { offset += interactedDash; return <circle cx="80" cy="80" r={radius} fill="none" stroke="#a855f7" strokeWidth="20" strokeDasharray={`${scrapedDash} ${circumference}`} strokeDashoffset={-offset} />; })()}
                          {privateUsers > 0 && (() => { offset += scrapedDash; return <circle cx="80" cy="80" r={radius} fill="none" stroke="#f59e0b" strokeWidth="20" strokeDasharray={`${privateDash} ${circumference}`} strokeDashoffset={-offset} />; })()}
                          {skippedUsers > 0 && (() => { offset += privateDash; return <circle cx="80" cy="80" r={radius} fill="none" stroke="#6b7280" strokeWidth="20" strokeDasharray={`${skippedDash} ${circumference}`} strokeDashoffset={-offset} />; })()}
                          {emptyUsers > 0 && (() => { offset += skippedDash; return <circle cx="80" cy="80" r={radius} fill="none" stroke="#ef4444" strokeWidth="20" strokeDasharray={`${emptyDash} ${circumference}`} strokeDashoffset={-offset} />; })()}
                          {otherVisited > 0 && (() => { offset += emptyDash; return <circle cx="80" cy="80" r={radius} fill="none" stroke="#3b82f6" strokeWidth="20" strokeDasharray={`${otherDash} ${circumference}`} strokeDashoffset={-offset} />; })()}
                          <text x="80" y="75" textAnchor="middle" fill="var(--text-primary)" fontSize="24" fontWeight="900" transform="rotate(90 80 80)">{totalSeen}</text>
                          <text x="80" y="92" textAnchor="middle" fill="var(--text-secondary)" fontSize="11" fontWeight="600" transform="rotate(90 80 80)">PROFILS</text>
                        </svg>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {legend.map((item, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: item.color }} />
                              <div style={{ color: 'var(--text-primary)', fontSize: '12px', fontWeight: '600' }}>
                                {item.label}: <span style={{ color: item.color, fontWeight: '700' }}>{item.pct}%</span>
                                <span style={{ color: 'var(--text-secondary)', fontSize: '10px', marginLeft: '4px' }}>({item.count})</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </>);
            })()}

            {/* Actions list */}
            <div style={{ marginBottom: '10px' }}>
              <div style={{ color: 'var(--text-secondary)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '8px' }}>
                Actions ({sessionActions.length})
              </div>
            </div>

            {loadingActions ? (
              <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>⏳ Chargement...</div>
            ) : sessionActions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>Aucune action trouvée</div>
            ) : (
              <ActionsList>
                {sessionActions.slice(0, 20).map((action, idx) => (
                  <ActionItem key={idx}>
                    <ActionType>
                      {getActionIcon(action.type)} {getActionLabel(action.type)}
                    </ActionType>
                    <ActionTarget>
                      @{action.target_user || 'N/A'}
                    </ActionTarget>
                    <ActionTime>
                      {new Date(action.timestamp).toLocaleTimeString('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </ActionTime>
                  </ActionItem>
                ))}
                {sessionActions.length > 20 && (
                  <div style={{ textAlign: 'center', padding: '8px', color: 'var(--text-muted)', fontSize: '11px' }}>
                    + {sessionActions.length - 20} actions supplémentaires
                  </div>
                )}
              </ActionsList>
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
            Sélectionnez une session pour voir les détails
          </div>
        )}
      </DetailPanel>
    </ContentLayout>
    </PageContainer>
  );
};

export default Sessions;
